import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';

const API_URL = process.env.REACT_APP_API_URL || '';

function formatDatum(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function AngeboteListe() {
  const navigate = useNavigate();
  const [angebote, setAngebote] = useState([]);
  const [typen, setTypen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [error, setError] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/api/angebote'),
      api.get('/api/angebote/typen'),
    ]).then(([ra, rt]) => {
      setAngebote(ra.data);
      setTypen(rt.data);
    }).catch(() => setError(true))
    .finally(() => setLoading(false));
  }, []);

  const filtered = filter
    ? angebote.filter(a => String(a.typ_id) === filter)
    : angebote;

  if (loading) return (
    <div className="tile-container">
      <div className="tile-grid tile-grid--skeleton">
        {[1,2,3].map(i => <div key={i} className="tile tile--skeleton" />)}
      </div>
    </div>
  );

  if (error) return (
    <div className="form-container">
      <p style={{ color: '#c62828', textAlign: 'center' }}>Angebote konnten nicht geladen werden.</p>
    </div>
  );

  return (
    <div className="form-container" style={{ maxWidth: 680 }}>
      <button className="back-btn" onClick={() => navigate('/')}>← Zurück</button>
      <h2 className="form-title">Angebote</h2>

      {/* Typ-Filter */}
      {typen.length > 1 && (
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
          <button
            onClick={() => setFilter('')}
            style={{
              padding: '4px 14px', borderRadius: 20, border: '1.5px solid',
              borderColor: !filter ? 'var(--primary, #0099cc)' : '#ddd',
              background: !filter ? 'var(--primary, #0099cc)' : '#fff',
              color: !filter ? '#fff' : '#555',
              fontWeight: !filter ? 700 : 400, cursor: 'pointer', fontSize: '0.85rem',
            }}>
            Alle
          </button>
          {typen.map(t => (
            <button key={t.id} onClick={() => setFilter(String(t.id))}
              style={{
                padding: '4px 14px', borderRadius: 20, border: '1.5px solid',
                borderColor: filter === String(t.id) ? t.farbe : '#ddd',
                background: filter === String(t.id) ? t.farbe : '#fff',
                color: filter === String(t.id) ? '#fff' : '#555',
                fontWeight: filter === String(t.id) ? 700 : 400, cursor: 'pointer', fontSize: '0.85rem',
              }}>
              {t.icon} {t.name}
            </button>
          ))}
        </div>
      )}

      {filtered.length === 0 && (
        <p style={{ color: '#888', textAlign: 'center', padding: '2rem' }}>
          Aktuell keine Angebote verfügbar.
        </p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
        {filtered.map(a => {
          const istVoll = a.max_teilnehmer && parseInt(a.anmeldungen_count) >= parseInt(a.max_teilnehmer);
          const restplaetze = a.max_teilnehmer ? parseInt(a.max_teilnehmer) - parseInt(a.anmeldungen_count) : null;

          return (
            <div key={a.id}
              onClick={() => navigate(`/angebote/${a.id}`)}
              role="button" tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && navigate(`/angebote/${a.id}`)}
              style={{
                display: 'flex', gap: '0.875rem', padding: '0.875rem',
                background: '#fff', borderRadius: 10,
                border: '1.5px solid #e8edf2', cursor: 'pointer',
                transition: 'box-shadow 0.15s, border-color 0.15s',
                boxShadow: '0 1px 4px rgba(0,0,0,.06)',
              }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,.12)'; e.currentTarget.style.borderColor = '#0099cc'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,.06)'; e.currentTarget.style.borderColor = '#e8edf2'; }}>

              {/* Bild/Icon */}
              <div style={{
                width: 72, height: 72, borderRadius: 8, flexShrink: 0, overflow: 'hidden',
                background: a.typ_farbe || '#0099cc',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem',
              }}>
                {a.bild_pfad
                  ? <img src={`${API_URL}/uploads/${a.bild_pfad}`} alt={a.titel} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : (a.typ_icon || '📌')}
              </div>

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                {a.typ_name && (
                  <span style={{
                    fontSize: '0.72rem', fontWeight: 700, padding: '1px 7px', borderRadius: 10,
                    background: (a.typ_farbe || '#0099cc') + '22',
                    color: a.typ_farbe || '#0099cc',
                    display: 'inline-block', marginBottom: 4,
                  }}>{a.typ_icon} {a.typ_name}</span>
                )}
                <div style={{ fontWeight: 700, fontSize: '1rem', color: '#003366', marginBottom: '0.1rem' }}>{a.titel}</div>
                {a.untertitel && <div style={{ fontSize: '0.82rem', color: '#666' }}>{a.untertitel}</div>}
                <div style={{ display: 'flex', gap: '0.875rem', fontSize: '0.78rem', color: '#888', marginTop: '0.35rem', flexWrap: 'wrap' }}>
                  {a.erster_termin && <span>📅 {formatDatum(a.erster_termin)}</span>}
                  {a.ort && <span>📍 {a.ort}</span>}
                  {a.preis_mitglied || a.preis_gast
                    ? <span>💶 ab {Number(a.preis_mitglied || a.preis_gast).toFixed(2)} €</span>
                    : <span style={{ color: '#2e7d32' }}>💶 Kostenlos</span>}
                </div>
              </div>

              {/* Status / Pfeil */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'space-between', flexShrink: 0 }}>
                {istVoll ? (
                  <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: '#ffebee', color: '#c62828' }}>
                    {a.warteliste_aktiv ? 'Warteliste' : 'Ausgebucht'}
                  </span>
                ) : restplaetze !== null && restplaetze <= 5 ? (
                  <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: '#fff8e1', color: '#e65100' }}>
                    Nur noch {restplaetze} Platz{restplaetze !== 1 ? 'e' : ''}
                  </span>
                ) : (
                  <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: '#e8f5e9', color: '#2e7d32' }}>
                    Verfügbar
                  </span>
                )}
                <span style={{ color: '#0099cc', fontWeight: 700, fontSize: '1.1rem' }}>→</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
